/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useInvoice } from "../lib/react-query/useInvoice";
import { formatBookingResponse } from "../utils/helpers/formatBookingResponse";
import { INVOICE_STATUS } from "../constants/constants";
import { useBookingApiEntities } from "./useBookingApiEntities";
import { useGetBookings } from "../lib/react-query/useGetBookings";

export const useBookingData = ({ invoiceId, bookingResponse }) => {
  const { includeEntities: bookingIncludeEntities } = useBookingApiEntities("booking");
  const { includeEntities: invoiceIncludeEntities } = useBookingApiEntities("invoice");

  const {
    data: booking,
    isLoading: isLoadingBooking,
    error: bookingError,
    isError: isBookingError,
  } = useGetBookings(
    {
      "filter[id]": bookingResponse?.bookings?.map((booking) => booking.id),
      include: bookingIncludeEntities,
    },
    {
      enabled: Boolean(bookingResponse) && !Boolean(invoiceId),
    }
  );

  const {
    data: invoice,
    isLoading: isInvoiceFirstLoad,
    isFetching: isFetchingInvoice,
    isFetchedAfterMount: isInvoiceFetchedAfterMount,
    isError: isInvoiceError,
    error: invoiceError,
  } = useInvoice(
    invoiceId,
    {
      include: invoiceIncludeEntities,
    },
    {
      enabled: Boolean(invoiceId) && !Boolean(bookingResponse),
      refetchInterval: (data) => {
        if (!data?.state?.data) return false;
        const invoiceStatus = data.state.data.status;
        return invoiceStatus === INVOICE_STATUS.PENDING ? 3000 : false;
      },
    }
  );

  // A cached invoice can predate the payment that led here - wait for the mount refetch, not the polls.
  const isLoadingInvoice = isInvoiceFirstLoad || (isFetchingInvoice && !isInvoiceFetchedAfterMount);

  const bookings = useMemo(() => {
    if (bookingResponse && booking?.data) {
      const formatted = formatBookingResponse({ response: booking.data, isInvoice: false });

      if (bookingResponse._productsMap) {
        return formatted.map(b => ({
          ...b,
          products: b.products?.length > 0 ? b.products : (bookingResponse._productsMap[b.service?.id] || []),
        }));
      }

      return formatted;
    }

    if (invoiceId && invoice) {
      return formatBookingResponse({ response: invoice, isInvoice: true });
    }

    return [];
  }, [booking, bookingResponse, invoice, invoiceId]);

  const firstBooking = useMemo(() => bookings?.[0] || null, [bookings]);
  const lastBooking = useMemo(() => bookings?.[bookings.length - 1] || null, [bookings]);

  const resolvedInvoice = invoice ?? firstBooking?.invoice ?? null;

  const isLoading = isLoadingBooking || isLoadingInvoice;
  const isError = isBookingError || isInvoiceError;
  const error = bookingError || invoiceError;

  return {
    booking,
    invoice: resolvedInvoice,

    bookings,
    firstBooking,
    lastBooking,

    isLoading,
    isError,
    error,
  };
};
